import React, { useMemo, useCallback } from "react"
import { useFeature } from "flagged"

import SmartMatchingToggle from "./SmartMatchingToggle"
import SmartMatchingScoreFilter from "./SmartMatchingScoreFilter"
import { isScoreInSelectedLevels } from "./helpers"
import { SmartMatchingData } from "./SmartMatchingContext"

import styles from "./SmartMatchingControls.module.css"
import cc from "classcat"
import { useAppSelector } from "~/hooks/redux"
import { setSelectedScoreLevels } from "~/Planner/reducer2/smartMatchingSlice"
import { useDispatch } from "react-redux"

type Props = {
  smartMatchingData?: SmartMatchingData
}

const SmartMatchingControls = ({ smartMatchingData }: Props) => {
  const isSmartMatchingFeatureEnabled = useFeature("smart_matching")
  const smartMatchingEnabled = useAppSelector(
    (state) => state.plannerV2.smartMatching.enabled,
  )
  const selectedScoreLevels = useAppSelector(
    (state) => state.plannerV2.smartMatching.selectedScoreLevels,
  )
  const dispatch = useDispatch()
  const updateSelectedScoreLevels = useCallback(
    (scoreLevels: typeof selectedScoreLevels) => {
      dispatch(setSelectedScoreLevels(scoreLevels))
    },
    [dispatch],
  )

  const resultsCount = smartMatchingData?.people?.length ?? 0
  const filteredResultsCount = useMemo(() => {
    if (!resultsCount) return 0

    return smartMatchingData.people.filter((person) => {
      return isScoreInSelectedLevels(person.totalScore, selectedScoreLevels)
    }).length
  }, [smartMatchingData, selectedScoreLevels, resultsCount])

  if (!isSmartMatchingFeatureEnabled) {
    return null
  }

  return (
    <div className={styles.secondaryControls}>
      <div className={styles.column}>
        <SmartMatchingToggle />
      </div>
      {smartMatchingEnabled && (
        <div className={styles.column}>
          <span className={styles.sortingLabel}>
            <span>Sorting by: </span> Availability
          </span>
        </div>
      )}
      {smartMatchingEnabled && smartMatchingData && (
        <div className={cc([styles.column, styles.scoreFilter])}>
          <SmartMatchingScoreFilter
            selectedScoreLevels={selectedScoreLevels}
            onSelectionChange={updateSelectedScoreLevels}
          />
        </div>
      )}
      {smartMatchingEnabled && smartMatchingData && (
        <div className={cc([styles.column, styles.results])}>
          <span className={styles.resultsCounter}>
            <span className={styles.resultsCounterActual}>
              {filteredResultsCount}
            </span>{" "}
            of {resultsCount}
          </span>
        </div>
      )}
    </div>
  )
}

export default SmartMatchingControls
